const pageUI = {
    scrollObserver: null,
    sectionInitObserver: null,

    init() {
        this.initHeaderScroll();
        this.initBackToTop();
        this.initScrollTo();
        this.initScrollAnimation();
    },

    initHeaderScroll() {
        const header = document.getElementById('js-header');
        const mainContent = document.getElementById('MainContent');
        if (!header) return;

        const isTemplatePage =
            window.location.pathname.includes('/pages/templates/') ||
            window.location.pathname.includes('/pages/hypothesis/');

        const hideThreshold = 500;

        let lastScrollY = window.scrollY;
        let offset = 0;
        let headerHeight = header.offsetHeight;
        let ticking = false;

        if (mainContent) {
            mainContent.style.paddingTop = `${headerHeight}px`;
        }

        const updateHeader = () => {
            const currentScrollY = window.scrollY;
            const delta = currentScrollY - lastScrollY;

            if (isTemplatePage) {
                offset = currentScrollY > hideThreshold ? headerHeight : 0;
            } else {
                if (delta > 0) {
                    offset += delta;
                    if (offset > headerHeight) offset = headerHeight;
                } else {
                    offset += delta;
                    if (offset < 0) offset = 0;
                }
            }

            header.style.transform = `translateY(-${offset}px)`;
            lastScrollY = currentScrollY <= 0 ? 0 : currentScrollY;
            ticking = false;
        };

        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(updateHeader);
            }
        };

        const onResize = () => {
            headerHeight = header.offsetHeight;
            if (mainContent) {
                mainContent.style.paddingTop = `${headerHeight}px`;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
    },

    initBackToTop() {
        const btn = document.getElementById('backToTop');
        if (!btn) return;

        const circle = btn.querySelector('circle');
        if (!circle) return;

        const RADIUS = 44;
        const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
        const SHOW_OFFSET = 300;

        let ticking = false;

        circle.style.strokeDasharray = CIRCUMFERENCE;
        circle.style.strokeDashoffset = CIRCUMFERENCE;

        const render = (progress) => {
            circle.style.strokeDashoffset = CIRCUMFERENCE * (1 - progress);
        };

        const update = () => {
            const scrollTop = window.scrollY;
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

            btn.classList.toggle('show', scrollTop > SHOW_OFFSET);

            const progress = maxScroll > 0
                ? Math.min(scrollTop / maxScroll, 1)
                : 0;

            render(progress);
            ticking = false;
        };

        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(update);
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });

        btn.addEventListener('click', () => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });

        update();
    },

    initScrollTo() {
        document.querySelectorAll('a[href^="#"]').forEach((link) => {
            link.addEventListener('click', (e) => {
                const href = link.getAttribute('href');
                if (!href) return;

                e.preventDefault();

                if (href === '#') {
                    history.replaceState(null, '', '#');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    return;
                }

                const target = document.getElementById(href.slice(1));
                if (!target) return;

                const header = document.querySelector('header');
                const offset = header?.offsetHeight || 0;

                const top =
                    target.getBoundingClientRect().top +
                    window.pageYOffset -
                    offset;

                window.scrollTo({
                    top,
                    behavior: 'smooth'
                });

                setTimeout(() => {
                    history.replaceState(null, '', href);
                }, 50);
            });
        });
    },

    initScrollAnimation() {
        if (!this.scrollObserver) {
            this.scrollObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                    }
                });
            }, {
                rootMargin: "50px 0px"
            });
        }

        const selectors = '.fade-in-up:not([data-ani-init]), .animation-bounce-in:not([data-ani-init])';

        document.querySelectorAll(selectors).forEach(section => {
            section.setAttribute('data-ani-init', 'true');
            this.scrollObserver.observe(section);
        });
    },

    initSectionOnView(target, callback) {
        const section =
            typeof target === 'string' ? document.querySelector(target) : target;

        if (!section || typeof callback !== 'function') return;

        if (!this.sectionInitObserver) {
            this.sectionInitObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) return;

                    const sectionCallback = entry.target._sectionInitCallback;
                    if (typeof sectionCallback === 'function') {
                        sectionCallback(entry.target, entry);
                    }

                    observer.unobserve(entry.target);
                    delete entry.target._sectionInitCallback;
                });
            }, {
                threshold: 0.1
            });
        }

        if (section.dataset.sectionInit === 'true') return;

        section.dataset.sectionInit = 'true';
        section._sectionInitCallback = callback;
        this.sectionInitObserver.observe(section);
    },
};


/***
  * Menu mobile
*/
const MobileDrawer = {
    init() {
        const burger = document.getElementById('js-open-menu');
        const drawer = document.getElementById('js-mobileDrawer');
        const close = document.getElementById('js-drawerClose');
        const acc = document.getElementById('js-featuresAcc');

        if (!burger || !drawer) return;

        const open = () => {
            drawer.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            burger.setAttribute('aria-expanded', 'true');
            document.body.classList.add('body-hidden');
        };

        const closeDrawer = () => {
            drawer.classList.remove('is-open');
            drawer.setAttribute('aria-hidden', 'true');
            burger.setAttribute('aria-expanded', 'false');
            document.body.classList.remove('body-hidden');
        };

        burger.addEventListener('click', open);

        close?.addEventListener('click', closeDrawer);

        drawer.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', closeDrawer);
        });

        acc?.querySelector('.mobile-acc__head')?.addEventListener('click', () => {
            acc.classList.toggle('is-open');
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                closeDrawer();
            }
        });
    }
};

function lazyLoadBackground(selector = '.lazy-bg', rootMargin = '300px 0px') {
    const lazyBgs = document.querySelectorAll(selector);

    const loadBg = (el) => {
        const bgUrl = el.dataset.bg;
        if (!bgUrl) return;

        const img = new Image();
        img.src = bgUrl;

        img.onload = () => {
            el.style.backgroundImage = `url(${bgUrl})`;
            el.classList.add('loaded');
        };
    };

    if (!('IntersectionObserver' in window)) {
        lazyBgs.forEach(loadBg);
        return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;

            loadBg(entry.target);
            obs.unobserve(entry.target);
        });
    }, { rootMargin });

    lazyBgs.forEach(bg => observer.observe(bg));
}

pageUI.initScrollAnimation();

document.addEventListener('DOMContentLoaded', () => {
    pageUI.init();

    MobileDrawer.init();

    lazyLoadBackground();
});